import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { identifyUserEvent } from '@/utils/account'
import { datadogRum } from '@datadog/browser-rum'

interface LoginCredentials {
  username: string
  password: string
  url?: string
  disableUserFetching?: boolean
  disableRedirect?: boolean
}

interface LoginResponse {
  token: string
  token_expires_at: string
}

interface AuthApi {
  login: (params: LoginCredentials) => Promise<LoginResponse>
  setUserToken: (token: string) => void
  logout: (params?: { redirectUrl?: string; keepToken?: boolean }) => void
}

export default defineNuxtPlugin((nuxtApp) => {
  const { fetchUser } = useAuth()
  const { setToken, setExpires, setUserId } = useApolloToken()

  const authApi: AuthApi = {
    login: async ({ username, password, url, disableUserFetching = false, disableRedirect = false }) => {
      const authStore = useAuthStore()
      const appStore = useAppStore()

      const response = await (authStore as any).login({ username, password })

      setToken(response.token)
      setExpires(response.token_expires_at)

      if (!disableUserFetching) {
        try {
          const user = await fetchUser()

          if (user?.user?._id) {
            identifyUserEvent({ otaUserId: `${user?.user?._id}` })
          }
        } catch (_error) {
          setToken(null)
          setExpires(null)
        }
      }

      const route = useRoute()
      if (!route.query?.chatId && !disableRedirect) {
        const next = route.query?.next ? route.query.next : url
        navigateTo(`/${appStore.locale}-${appStore.country}${next}`)
      }

      if (authStore.redirectUrl !== '/account') {
        authStore.setRedirectUrl('/account')
      }

      return response
    },

    setUserToken: (token: string) => {
      const authStore = useAuthStore()

      setToken(token)

      authStore.setAuthToken(token, '')
    },

    logout: ({ redirectUrl = '/login', keepToken = false } = {}) => {
      const authStore = useAuthStore()
      const appStore = useAppStore()
      const userStore = useUserStore()

      if (!keepToken) {
        userStore.deleteSession().catch((deleteSessionError) => {
          useNuxtApp().$ota.general.logError(deleteSessionError)
        })
      }

      authStore.logout()

      setExpires(null)
      setToken(null)
      setUserId(null)

      datadogRum.clearUser()

      navigateTo(`/${appStore.locale}-${appStore.country}${redirectUrl}`)
    }
  }

  nuxtApp.provide('auth', authApi)
})
